import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ListOrdered,
  LogOut,
  MapPin,
  Play,
  RefreshCw,
  Route,
  Sparkles,
  WifiOff,
  type LucideIcon,
} from 'lucide-react';
import { userDataErrorTitle, type UserSyncSummary } from '../../api/userDataApi';
import { useAuth } from '../../auth/AuthContext';
import { useUserSyncData } from '../../hooks/useUserSyncData';
import ThemeToggle from '../../theme/ThemeToggle';
import './preview-home.css';

function displayName(fullName: string, email: string): string {
  const trimmed = fullName.trim();
  if (trimmed) {
    return trimmed.split(/\s+/)[0];
  }
  const local = email.split('@')[0]?.trim();
  return local || 'there';
}

function PreviewMark() {
  return (
    <svg className="preview-hero__svg" viewBox="0 0 360 220" aria-hidden="true">
      <ellipse cx="300" cy="36" rx="22" ry="22" fill="var(--ml-accent-soft, #ffe08a)" />
      <path
        d="M0 148c36-24 64-6 100-20 34-12 50 10 86-4 32-12 56 8 88-8 26-12 44 2 86-8v112H0z"
        fill="#b7e7c2"
      />
      <path
        d="M0 172c46-16 74 10 118-2 38-12 64 14 108-4 36-14 64 8 134-10v64H0z"
        fill="#8fd39a"
      />
      <path
        d="M48 150c36-30 70-16 102-40 26-20 52-14 86 6 22 12 42 8 70-4"
        fill="none"
        stroke="var(--ml-primary, #7c5cff)"
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray="2 8"
      />
      <g>
        <path d="M168 78c-12 18-22 30-22 42a22 22 0 0 0 44 0c0-12-10-24-22-42z" fill="var(--ml-primary, #7c5cff)" />
        <circle cx="168" cy="116" r="7" fill="#fff" />
      </g>
      <g>
        <path d="M246 104c-9 14-16 22-16 32a16 16 0 0 0 32 0c0-10-7-18-16-32z" fill="var(--ml-info, #3b82f6)" />
        <circle cx="246" cy="134" r="5" fill="#fff" />
      </g>
    </svg>
  );
}

type StatItem = {
  label: string;
  value: number;
  hint: string;
  icon: LucideIcon;
};

function statItems(data: UserSyncSummary): StatItem[] {
  return [
    {
      label: 'Saved points',
      value: data.stationaryPointsCount,
      hint: data.favoritePointsCount === 1 ? '1 favourite' : `${data.favoritePointsCount} favourites`,
      icon: MapPin,
    },
    {
      label: 'Playlists',
      value: data.playlistsCount,
      hint: data.playlistPointsCount === 1 ? '1 point inside' : `${data.playlistPointsCount} points inside`,
      icon: ListOrdered,
    },
    {
      label: 'Itineraries',
      value: data.itinerariesCount,
      hint: data.subItinerariesCount === 1 ? '1 stop' : `${data.subItinerariesCount} stops`,
      icon: Route,
    },
    {
      label: 'Launches',
      value: data.totalLaunches,
      hint: 'Points and routes played',
      icon: Play,
    },
  ];
}

const PreviewHome: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { state, reload } = useUserSyncData(user?.id);
  const email = user?.userEmail?.trim() || user?.googleMail?.trim() || '';
  const name = displayName(user?.userName ?? '', email);
  const summary = state.status === 'success' ? state.data : null;

  const signOut = () => {
    logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="preview">
      <header className="preview-bar">
        <div className="preview-brand">
          <img src="/mockloc-icon.png" alt="" className="preview-brand__logo" />
          <span>
            <strong>MockLoc</strong>
            <em>{email || 'Signed in'}</em>
          </span>
        </div>
        <div className="preview-bar__actions">
          <ThemeToggle />
          <button type="button" className="preview-signout" onClick={signOut}>
            <LogOut size={16} aria-hidden="true" />
            Sign out
          </button>
        </div>
      </header>

      <main className="preview-main">
        <section className="preview-hero">
          <div className="preview-hero__copy">
            <p className="preview-kicker">
              <Sparkles size={14} aria-hidden="true" />
              Developer at work
            </p>
            <h1>Hi {name}. The full site opens soon.</h1>
            <p>
              A developer is building this web panel for you. You will get the complete
              experience soon. Until then, this page is your home — a quiet look at the
              MockLoc data already on your account.
            </p>
          </div>
          <div className="preview-hero__art">
            <PreviewMark />
          </div>
        </section>

        <ol className="preview-steps">
          <li>
            <span>1</span>
            Account ready
          </li>
          <li>
            <span>2</span>
            {summary?.hasAnyData ? 'Data synced' : 'Sync from the app'}
          </li>
          <li className="is-current">
            <span>3</span>
            Web panel in progress
          </li>
        </ol>

        <section className="preview-stats" aria-labelledby="preview-stats-title">
          <div className="preview-stats__head">
            <h2 id="preview-stats-title">Your account, at a glance</h2>
            <p>Counts come from the data synced with your MockLoc account.</p>
          </div>

          {state.status === 'loading' || state.status === 'idle' ? (
            <div className="preview-grid" aria-busy="true" aria-label="Loading your stats">
              {['Saved points', 'Playlists', 'Itineraries', 'Launches'].map((label) => (
                <div key={label} className="preview-card preview-card--skeleton" aria-hidden="true">
                  <span />
                  <span />
                </div>
              ))}
            </div>
          ) : null}

          {state.status === 'error' ? (
            <div className="preview-error" role="alert">
              <WifiOff size={22} aria-hidden="true" />
              <div>
                <strong>{userDataErrorTitle(state.error.kind)}</strong>
                <p>{state.error.message}</p>
              </div>
              <button type="button" onClick={() => void reload()}>
                <RefreshCw size={16} aria-hidden="true" />
                Try again
              </button>
            </div>
          ) : null}

          {summary ? (
            <>
              <div className="preview-grid">
                {statItems(summary).map((item) => {
                  const Icon = item.icon;
                  return (
                    <article key={item.label} className="preview-card">
                      <span className="preview-card__icon" aria-hidden="true">
                        <Icon size={18} />
                      </span>
                      <p>{item.label}</p>
                      <strong>{item.value}</strong>
                      <em>{item.hint}</em>
                    </article>
                  );
                })}
              </div>
              {!summary.hasAnyData ? (
                <p className="preview-empty">
                  Nothing synced yet. Save a point in the Android app and it will show up here.
                </p>
              ) : null}
            </>
          ) : null}
        </section>
      </main>
    </div>
  );
};

export default PreviewHome;
